'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/providers/api-provider'
import type { Project } from '@/lib/api/types'

export function useProjects() {
  const api = useApi()
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
  })
}

export function useProject(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => api.projects.get(id),
    enabled: !!id,
  })
}

export function useProjectMembers(projectId: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['projects', projectId, 'members'],
    queryFn: () => api.projects.members.list(projectId),
    enabled: !!projectId,
  })
}

export function useCreateProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Project>) => api.projects.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) => api.projects.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['projects', id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useDeleteProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.projects.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useAddProjectMember() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: { email: string; role: string } }) =>
      api.projects.members.add(projectId, data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'members'] })
    },
  })
}

export function useRemoveProjectMember() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      api.projects.members.remove(projectId, userId),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'members'] })
    },
  })
}

export function useProjectActivity(projectId: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['projects', projectId, 'activity'],
    queryFn: () => api.projects.activity(projectId),
    enabled: !!projectId,
  })
}
